import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Truck } from 'lucide-react';
import { useUserData } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import type { OperationalLocation } from '../../../lib/types';

/**
 * Indirizzo di spedizione dell'offerta, dentro la card Cliente (sotto email,
 * telefono e sede): si sceglie tra gli indirizzi del cliente
 * (client_operational_locations — indirizzi di spedizione e sedi operative),
 * come già su ordini e DDT. Vuoto = sede del cliente.
 *
 * `autoSelectDefault`: su un'offerta nuova (o al cambio cliente) propone
 * l'indirizzo predefinito del cliente; in modifica il valore salvato non si tocca.
 * Gli indirizzi si ricaricano all'apertura del menu: un indirizzo appena
 * aggiunto nell'anagrafica compare senza ricaricare la pagina.
 *
 * `required` (Impostazioni → Offerte, indirizzo di spedizione obbligatorio,
 * richiesta Sayfarm): asterisco, proposto anche l'unico indirizzo del cliente
 * e «Sede del cliente» mostra l'indirizzo vero, che passa all'ordine e al DDT
 * (flusia-core orders/repository resolveDealShipping).
 */
interface DealShippingLocationSelectProps {
  clientId: string;
  value: string | null | undefined;
  onChange: (locationId: string | null) => void;
  autoSelectDefault: boolean;
  required?: boolean;
  /** Indirizzo della sede del cliente (via e città), mostrato per «Sede del cliente» */
  clientAddress?: string;
}

const SELECT_FIELDS = 'id, client_id, organization_id, name, address, city, province, postal_code, country, is_active, is_default, recipient_name, phone, location_type, created_at, updated_at';

export function DealShippingLocationSelect({ clientId, value, onChange, autoSelectDefault, required = false, clientAddress = '' }: DealShippingLocationSelectProps) {
  const {organizationId} = useUserData();
  const context = `${organizationId}:${clientId}`;
  const currentContext = useRef(context); currentContext.current = context;
  const [locations, setLocations] = useState<OperationalLocation[]>([]);
  const [loaded, setLoaded] = useState(false);
  const autoAppliedRef = useRef(false);

  const load = useCallback(async (applyDefault: boolean) => {
    if (!clientId || !organizationId) return;
    const { data, error } = await supabase
      .from('client_operational_locations')
      .select(SELECT_FIELDS)
      .eq('client_id', clientId)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });
    if(currentContext.current!==context) return;
    if (error) { console.warn('[DealShippingLocationSelect] indirizzi non caricati', error); return; }
    const list = (data || []) as OperationalLocation[];
    // Stessi indirizzi di prima: niente aggiornamento, il menu aperto non si ridisegna
    setLocations(prev => (JSON.stringify(prev) === JSON.stringify(list) ? prev : list));
    setLoaded(true);
    if (applyDefault && !value && !autoAppliedRef.current) {
      // Predefinito; con l'indirizzo obbligatorio anche l'unico indirizzo del cliente
      const def = list.find(l => l.is_default) || (required && list.length === 1 ? list[0] : undefined);
      if (def) { autoAppliedRef.current = true; onChange(def.id); }
    }
  }, [clientId, organizationId, context, value, onChange, required]);

  useEffect(() => {
    currentContext.current = context;
    autoAppliedRef.current = false;
    setLocations([]);
    setLoaded(false);
    load(autoSelectDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return()=>{currentContext.current='';};
  }, [clientId, organizationId]);

  if (!loaded) return null;

  const selected = locations.find(l => l.id === value);
  const selectedAddress = selected
    ? [selected.recipient_name, selected.address, [selected.postal_code, selected.city].filter(Boolean).join(' '), selected.province ? `(${selected.province})` : null]
        .filter(Boolean).join(', ')
    : '';

  return (
    <div className="pt-1">
      <div className="flex items-center gap-1.5 text-[11.5px] text-black/65 dark:text-white/65">
        <Truck className="h-3 w-3 flex-shrink-0 opacity-70" />
        <span className="flex-shrink-0">Indirizzo di consegna{required ? ' *' : ''}:</span>
        <select
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          // Ricarica all'apertura: un indirizzo aggiunto nel frattempo compare subito
          onMouseDown={() => { load(false); }}
          onFocus={() => { load(false); }}
          className="min-w-0 flex-1 truncate bg-transparent py-0.5 text-[11.5px] text-black/80 dark:text-white/80 border-0 border-b border-dashed border-black/15 dark:border-white/20 focus:outline-none focus:border-[#2383E2] cursor-pointer"
        >
          <option value="">
            {clientAddress
              ? `Sede del cliente · ${clientAddress}`
              : required ? 'Sede del cliente (via e città mancanti in anagrafica)' : 'Sede del cliente'}
          </option>
          {(['shipping', 'operational'] as const).map(type => {
            const group = locations.filter(l => (l.location_type || 'operational') === type);
            if (!group.length) return null;
            return (
              <optgroup key={type} label={type === 'shipping' ? 'Indirizzi di spedizione' : 'Sedi operative'}>
                {group.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name || l.address || 'Indirizzo'}{l.is_default ? ' · predefinito' : ''}{l.city ? ` · ${l.city}` : ''}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
      </div>
      {selectedAddress && (
        <p className="pl-[18px] text-[11px] text-black/45 dark:text-white/45 truncate">{selectedAddress}</p>
      )}
      {/* «Sede del cliente»: l'indirizzo che arriverà all'ordine e al DDT */}
      {!value && clientAddress && (
        <p className="pl-[18px] text-[11px] text-black/45 dark:text-white/45 truncate">{clientAddress}</p>
      )}
      {required && !value && !clientAddress && (
        <p className="pl-[18px] text-[11px] text-red-600 dark:text-red-400">
          Indirizzo obbligatorio: scegline uno del cliente oppure completa via e città nella sua anagrafica.
        </p>
      )}
      {locations.length === 0 && (clientAddress || !required) && (
        <p className="pl-[18px] text-[11px] text-black/40 dark:text-white/40">
          Nessun indirizzo di spedizione: aggiungilo nell'anagrafica del cliente.
        </p>
      )}
    </div>
  );
}

export default DealShippingLocationSelect;
